import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getFavorites, removeFromFavorites, clearFavorites } from '../utils/favoritesService';
import PaymentModal from './PaymentModal';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const PageHeader = styled.h1`
  font-size: 2rem;
  margin-bottom: 10px;
  position: sticky;
  top: 180px;
  background-color: #000;
  z-index: 100;
  padding: 15px 0;
  margin-top: -15px;

  @media (max-width: 768px) {
    top: 140px;
  }
`;

const PageDescription = styled.p`
  color: #aaa;
  margin-bottom: 30px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
`;

const ClearButton = styled.button`
  background: #444;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #666;
  }
`;

const NoFavoritesMessage = styled.div`
  padding: 30px;
  text-align: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 30px;
`;

const StartBrowsingLink = styled(Link)`
  display: inline-block;
  background: #2c7d46;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  margin-top: 15px;
  transition: background 0.2s ease;
  
  &:hover {
    background: #36964f;
  }
`;

const FavoritesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }
`;

const FavoriteCard = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const FavoriteImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
`;

const FavoriteImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${FavoriteCard}:hover & {
    transform: scale(1.05);
  }
`;

const FavoriteDetails = styled.div`
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const FavoriteTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 5px;
`;

const CollectionName = styled(Link)`
  font-size: 0.8rem;
  margin-bottom: 10px;
  color: #aaa;
  
  &:hover {
    color: #fff;
  }
`;

const FavoriteInfo = styled.div`
  margin-bottom: 10px;
  flex-grow: 1;
`;

const FavoriteDetail = styled.div`
  font-size: 0.9rem;
  color: #ddd;
  margin-bottom: 5px;
`;

const FavoritePrice = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  margin: 10px 0;
`;

const FavoriteActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const RemoveButton = styled.button`
  background: #444;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #666;
  }
`;

const PurchaseButton = styled.button`
  flex: 1;
  background: #2c7d46;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #36964f;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #a00;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #2c7d46;
  color: white;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
`;

function Favorites() {
  const [favorites, setFavorites] = useState([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  
  // Load favorites on component mount
  useEffect(() => {
    loadFavorites();
  }, []);
  
  const loadFavorites = () => {
    const savedFavorites = getFavorites();
    setFavorites(savedFavorites);
  };
  
  const handleRemove = (itemId) => {
    removeFromFavorites(itemId);
    loadFavorites();
  };
  
  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to remove all items from your favorites?')) {
      clearFavorites();
      loadFavorites();
    }
  };
  
  const handlePurchase = (item) => {
    setSelectedItem(item);
    setIsCheckingOut(true);
  };
  
  const handlePaymentSuccess = () => {
    setPaymentComplete(true);
    setIsCheckingOut(false);
    
    // Also remove the purchased item from favorites
    if (selectedItem) {
      removeFromFavorites(selectedItem.id);
      loadFavorites();
    }
  };
  
  const closePaymentModal = () => {
    setIsCheckingOut(false);
    setSelectedItem(null);
  };
  
  return (
    <PageContainer>
      <PageHeader>My Favorite Artwork</PageHeader>
      <PageDescription>
        Your saved artwork collection. You can view and purchase items from here, or remove them from your favorites.
      </PageDescription>
      
      {favorites.length > 0 && (
        <ActionButtons>
          <ClearButton onClick={handleClearAll}>Clear All Favorites</ClearButton>
        </ActionButtons>
      )}
      
      {favorites.length === 0 ? (
        <NoFavoritesMessage>
          <h3>You haven't saved any artwork yet</h3>
          <p>Browse the collections and click the heart icon to save your favorite pieces</p>
          <StartBrowsingLink to="/">Start Browsing</StartBrowsingLink>
        </NoFavoritesMessage>
      ) : (
        <FavoritesGrid>
          {favorites.map(item => (
            <FavoriteCard key={item.id}>
              <FavoriteImageContainer>
                <FavoriteImage src={item.imagePath} alt={item.title} />
                {!item.available && item.status && (
                  <StatusBadge>{item.status}</StatusBadge>
                )}
              </FavoriteImageContainer>
              
              <FavoriteDetails>
                <FavoriteTitle>{item.title}</FavoriteTitle>
                <CollectionName to={`/collection/${item.collectionId}`}>
                  {item.collectionTitle}
                </CollectionName>
                
                <FavoriteInfo>
                  {item.medium && (
                    <FavoriteDetail><strong>Medium:</strong> {item.medium}</FavoriteDetail>
                  )}
                  {item.size && (
                    <FavoriteDetail><strong>Size:</strong> {item.size}</FavoriteDetail>
                  )}
                  {item.year && (
                    <FavoriteDetail><strong>Year:</strong> {item.year}</FavoriteDetail>
                  )}
                </FavoriteInfo>
                
                {item.available && item.price && (
                  <FavoritePrice>${item.price}</FavoritePrice>
                )}
                
                <FavoriteActions>
                  <RemoveButton onClick={() => handleRemove(item.id)}>
                    Remove
                  </RemoveButton>
                  
                  <PurchaseButton 
                    onClick={() => handlePurchase(item)}
                    disabled={!item.available}
                  >
                    {item.available ? 'Purchase' : 'Not Available'}
                  </PurchaseButton>
                </FavoriteActions>
              </FavoriteDetails>
            </FavoriteCard>
          ))}
        </FavoritesGrid>
      )}
      
      {isCheckingOut && selectedItem && (
        <PaymentModal
          artwork={selectedItem}
          onClose={closePaymentModal}
          onSuccess={handlePaymentSuccess}
        />
      )}
      
      {paymentComplete && (
        <SuccessMessage>
          Thank you for your purchase! We will contact you shortly to arrange delivery.
        </SuccessMessage>
      )}
    </PageContainer>
  );
}

export default Favorites;